import React, { useState, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Shield, Users, Layers, Lock } from 'lucide-react';
// 🛰️ Using unified types to resolve property mismatches
import { KanbanTask, BoardConfig, TaskStatus, Role } from '@/types';
import { TaskCard } from './TaskCard';

interface KanbanBoardProps {
  config: BoardConfig;
  tasks: KanbanTask[];
  onMoveTask: (taskId: string, newStatus: TaskStatus, targetScope: 'personal' | 'household') => void;
  // 🛡️ Hybrid Scoping Props
  currentUserId: string;
  currentHouseholdId: string | null;
  userPermissions?: any; // Derived from household permission matrix
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({ 
  config, 
  tasks, 
  onMoveTask,
  currentUserId,
  currentHouseholdId,
  userPermissions
}) => {
  // 🛡️ Scope state: Defaults to 'both' if in a household, otherwise locked to 'personal'
  const [activeScope, setActiveScope] = useState<'personal' | 'household' | 'both'>(
    currentHouseholdId ? 'both' : 'personal'
  );

  /**
   * 🛡️ HYBRID FILTERING ENGINE
   * Respects privacy boundaries: ensures personal tasks from other members are never visible.
   */
  const filteredTasks = useMemo(() => {
    return (tasks || []).filter((t: KanbanTask) => {
      const isCorrectBoard = t.boardId === config.id && !t.isDeleted;
      
      let scopeMatches = true;
      if (activeScope === 'personal') scopeMatches = t.scope === 'personal';
      if (activeScope === 'household') scopeMatches = t.scope === 'household';

      // 🔒 Privacy Guard: Never leak personal tasks of other users
      const privacyCheck = t.scope === 'personal' ? t.ownerId === currentUserId : true;

      return isCorrectBoard && scopeMatches && privacyCheck;
    });
  }, [tasks, config.id, activeScope, currentUserId]);

  const renderColumn = (status: TaskStatus) => {
    const columnTasks = filteredTasks.filter((t: KanbanTask) => t.status === status);
    
    const { setNodeRef, isOver } = useDroppable({
      id: `${config.id}-${status}`, 
      data: { board: config, status: status }
    });
    
    const getColumnTitle = (s: TaskStatus): string => {
      const titles: Record<string, string> = {
        todo: config.templateType === 'grocery' ? 'Needed' : 'To-Do',
        in_progress: config.templateType === 'grocery' ? 'In Cart' : 'Active',
        pantry: 'Pantry',
        finalized: 'Finalized',
        history: 'Registry'
      };
      return titles[s as string] || (s as string).toUpperCase();
    };

    return (
      <div 
        ref={setNodeRef}
        key={status} 
        className={`flex-1 min-w-[320px] rounded-[2rem] p-5 flex flex-col h-full border-2 transition-all duration-300 ${
          isOver 
            ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-400 shadow-xl' 
            : 'bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800'
        }`}
      >
        <div className="flex justify-between items-center mb-6 px-3">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isOver ? 'bg-indigo-500 animate-ping' : 'bg-slate-300 dark:bg-slate-700'}`} />
            <h2 className="font-black text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">
              {getColumnTitle(status)}
            </h2>
          </div>
          <span className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-[10px] px-3 py-1 rounded-xl font-black shadow-sm border border-slate-100 dark:border-slate-700">
            {columnTasks.length}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 no-scrollbar">
          <SortableContext 
            items={columnTasks.map((t: KanbanTask) => t.id)} 
            strategy={verticalListSortingStrategy}
          >
            {columnTasks.map((task: KanbanTask) => (
              <TaskCard 
                key={task.id} 
                task={task} 
                onMove={(id, nextStatus) => onMoveTask(id, nextStatus, task.scope as any)}
              />
            ))}
          </SortableContext>
          
          {columnTasks.length === 0 && (
            <div className={`border-2 border-dashed rounded-3xl h-32 flex flex-col items-center justify-center gap-2 transition-all ${
              isOver ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-100 dark:border-slate-800 opacity-50'
            }`}>
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest italic">
                {isOver ? 'Release to Move' : 'Empty Scope'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full space-y-6">
      {/* --- BOARD HEADER & HYBRID TOGGLE --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between px-4 gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase italic tracking-tighter">
            {config.templateType === 'grocery' ? '🛒' : '⚡'} {config.title}
          </h1>
          {config.visibility === 'admin' && (
             <span className="text-[9px] bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-500 px-3 py-1 rounded-full font-black uppercase tracking-widest border border-amber-100 dark:border-amber-800">
               Privileged Access
             </span>
          )}
        </div>

        {/* 🛡️ HYBRID SCOPE SELECTOR */}
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl gap-1 self-start">
          {[
            { id: 'personal', icon: Shield, label: 'Personal' },
            { id: 'household', icon: Users, label: 'Shared' },
            { id: 'both', icon: Layers, label: 'Both' }
          ].map((s) => (
            <button
              key={s.id}
              disabled={s.id === 'household' && !currentHouseholdId}
              onClick={() => setActiveScope(s.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeScope === s.id 
                  ? 'bg-white dark:bg-slate-700 shadow-md text-indigo-600' 
                  : 'text-slate-400 hover:text-slate-500'
              } ${s.id === 'household' && !currentHouseholdId ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <s.icon className="w-3 h-3" />
              <span className="hidden sm:inline">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* --- HORIZONTAL COLUMN CONTAINER --- */}
      <div className="flex gap-6 overflow-x-auto pb-8 h-[calc(100vh-300px)] items-start no-scrollbar px-2">
        {config.allowedStatuses.map((status: TaskStatus) => renderColumn(status))}
      </div>
    </div>
  );
};